import { Line, Rectangle, Boundary, Vector, Circle, Polygon, Point } from '../interface';

// ! SAT Algorithm
export function intersectionPP(polygonA: Polygon, polygonB: Polygon): boolean {
  const radians: Array<number> = getPolygonRadians(polygonA, polygonB);
  let isCollision: boolean = true;

  for (const radian of radians) {
    const boundary = intersectionBoundary(normalizePolygon(polygonA, radian), normalizePolygon(polygonB, radian));

    isCollision &&= boundary.min - boundary.max < Number.EPSILON;

    if (!isCollision) {
      return false;
    }
  }

  return true;
}

// ! Ray Casting
export function intersectionLL(lineA: Line, lineB: Line): number {
  const det =
    (lineA[1].x - lineA[0].x) * (lineB[1].y - lineB[0].y) - (lineB[1].x - lineB[0].x) * (lineA[1].y - lineA[0].y);

  if (det === 0) {
    return -1;
  } else {
    const t1 =
      ((lineB[1].y - lineB[0].y) * (lineB[1].x - lineA[0].x) + (lineB[0].x - lineB[1].x) * (lineB[1].y - lineA[0].y)) /
      det;
    const t2 =
      ((lineA[0].y - lineA[1].y) * (lineB[1].x - lineA[0].x) + (lineA[1].x - lineA[0].x) * (lineB[1].y - lineA[0].y)) /
      det;

    return 0 <= t1 && t1 <= 1 && 0 <= t2 && t2 <= 1 ? t1 : -1;
  }
}

export function intersectionLC(line: Line, circle: Circle): number {
  const diffA = Math.sqrt((line[0].x - circle.x) ** 2 + (line[0].y - circle.y) ** 2);
  const diffB = Math.sqrt((line[1].x - circle.x) ** 2 + (line[1].y - circle.y) ** 2);

  if (diffA <= circle.r || diffB <= circle.r) {
    // TODO: Detail DT
    return 1;
  } else {
    const radian = Math.atan2(line[1].y - line[0].y, line[1].x - line[0].x) + Math.PI / 2;
    return intersectionLL(line, [
      {
        x: circle.x + circle.r * Math.cos(radian),
        y: circle.y + circle.r * Math.sin(radian),
      },
      {
        x: circle.x + circle.r * Math.cos(radian + Math.PI),
        y: circle.y + circle.r * Math.sin(radian + Math.PI),
      },
    ]);
  }
}

export function intersectionVSVS(
  dt: number,
  squareA: Rectangle,
  velocityA: Vector,
  squareB: Rectangle,
  velocityB: Vector
): { hasIntersect: boolean; dt: [number, number] } {
  const xAxis = intersectionAxisXVSVS(squareA, velocityA, squareB, velocityB);
  const yAxis = intersectionAxisYVSVS(squareA, velocityA, squareB, velocityB);
  const hasIntersect =
    xAxis.min <= dt &&
    xAxis.max >= 0 &&
    yAxis.min <= dt &&
    yAxis.max >= 0 &&
    yAxis.max >= xAxis.min &&
    xAxis.max >= yAxis.min;

  return { hasIntersect, dt: [xAxis.min, yAxis.min] };
}

export function intersectionBoundary(boundaryA: Boundary<number>, boundaryB: Boundary<number>): Boundary<number> {
  return {
    min: Math.max(boundaryA.min, boundaryB.min),
    max: Math.min(boundaryA.max, boundaryB.max),
  };
}

function intersectionAxisYVSVS(
  squareA: Rectangle,
  velocityA: Vector,
  squareB: Rectangle,
  velocityB: Vector
): Boundary<number> {
  const velocityDiff = velocityB.y - velocityA.y;
  const axisDiffA = squareA.y - (squareB.y + squareB.h);
  const axisDiffB = squareA.y + squareA.h - squareB.y;

  if (velocityDiff === 0 && axisDiffB >= 0 && axisDiffA <= 0) {
    return { min: -Infinity, max: Infinity };
  } else if (velocityDiff > 0) {
    return { min: axisDiffA / velocityDiff, max: axisDiffB / velocityDiff };
  } else if (velocityDiff < 0) {
    return { min: axisDiffB / velocityDiff, max: axisDiffA / velocityDiff };
  } else {
    return { min: Infinity, max: -Infinity };
  }
}

function intersectionAxisXVSVS(
  squareA: Rectangle,
  velocityA: Vector,
  squareB: Rectangle,
  velocityB: Vector
): Boundary<number> {
  const velocityDiff = velocityB.x - velocityA.x;
  const axisDiffA = squareA.x - (squareB.x + squareB.w);
  const axisDiffB = squareA.x + squareA.w - squareB.x;

  if (velocityDiff === 0 && axisDiffB >= 0 && axisDiffA <= 0) {
    return { min: -Infinity, max: Infinity };
  } else if (velocityDiff > 0) {
    return { min: axisDiffA / velocityDiff, max: axisDiffB / velocityDiff };
  } else if (velocityDiff < 0) {
    return { min: axisDiffB / velocityDiff, max: axisDiffA / velocityDiff };
  } else {
    return { min: Infinity, max: -Infinity };
  }
}

function getRadian(points: [Point, Point]): number {
  return Math.atan2(points[1].y - points[0].y, points[1].x - points[0].x);
}

function getPolygonRadians(...polygons: Array<Polygon>): Array<number> {
  const radians: Array<number> = [];
  const dict: Record<string | number, boolean> = {};

  for (const polygon of polygons) {
    for (let i = 0; i < polygon.length; i++) {
      const point = polygon[i];
      const nextPoint = polygon[(i + 1) % polygon.length];
      const radian = (getRadian([point, nextPoint]) + Math.PI) % Math.PI;

      if (!dict[radian]) {
        dict[radian] = true;
        radians.push(radian);
      }
    }
  }

  return radians;
}

function rotatePoint(point: Point, radian: number): Point {
  return {
    x: point.x * Math.cos(radian) - point.y * Math.sin(radian),
    y: point.x * Math.sin(radian) + point.y * Math.cos(radian),
  };
}

function normalizePolygon(polygon: Polygon, radian: number): Boundary<number> {
  const boundary: Boundary<number> = { min: Infinity, max: -Infinity };
  for (const point of polygon) {
    const rotatedPoint: Point = rotatePoint(point, -radian);

    if (boundary.min > rotatedPoint.x) {
      boundary.min = rotatedPoint.x;
    }
    if (boundary.max < rotatedPoint.x) {
      boundary.max = rotatedPoint.x;
    }
  }

  return boundary;
}
